import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlatformBadge } from "@/components/Badges";
import { api, errMsg, money } from "@/lib/api";

export const ManualMatchDialog = ({ depot, reservations, onClose, onDone }) => {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const candidates = useMemo(
    () => reservations.filter((r) => !depot?.plateforme_detectee || r.plateforme === depot.plateforme_detectee),
    [reservations, depot]
  );
  const total = candidates.filter((r) => selected.includes(r.id)).reduce((s, r) => s + r.total_verse, 0);
  const ecart = depot ? depot.credit - total : 0;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/matches", { bank_tx_id: depot.id, reservation_ids: selected });
      toast.success("Rapprochement enregistré");
      setSelected([]);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!depot} onOpenChange={(o) => { if (!o) { setSelected([]); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="manual-match-dialog">
        <DialogHeader>
          <DialogTitle>Associer manuellement</DialogTitle>
          <DialogDescription>
            Dépôt du {depot?.date} · {depot?.description} · <span className="tabular font-semibold">{money(depot?.credit)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 rounded-md border">
          {candidates.length === 0 && <p className="p-4 text-sm text-slate-500">Aucune réservation libre.</p>}
          {candidates.map((r) => (
            <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`manual-match-option-${r.id}`}>
              <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`manual-match-checkbox-${r.id}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{r.code} <span className="text-xs text-slate-500">· {r.date}</span></p>
                <PlatformBadge plateforme={r.plateforme} />
              </div>
              <span className="tabular text-sm">{money(r.total_verse)}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-between text-sm">
          <span>Sélection : <b className="tabular">{money(total)}</b></span>
          <span className={Math.abs(ecart) <= 2 ? "text-emerald-700" : "text-amber-700"} data-testid="manual-match-ecart">Écart : <b className="tabular">{money(ecart)}</b></span>
        </div>
        <DialogFooter>
          <Button disabled={!selected.length || loading} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800" data-testid="manual-match-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Valider l'association
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
